import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';
import { useMyLeagueStore, isLineupEditingDisabled } from '../store/myLeagueStore';
import { useUserStore } from '../store/userStore';

function assertLineupsEditable(): void {
  const league = useMyLeagueStore.getState().myLeague;
  if (isLineupEditingDisabled(league)) {
    throw new Error('Lineups are currently locked.');
  }
}

/** Admin/support may view other teams’ lineups but cannot save changes from the client. */
function assertCanMutateLineupForTeam(teamId: string): void {
  const user = useUserStore.getState().user;
  const role = user?.role?.toUpperCase();
  const isStaff = role === 'ADMIN' || role === 'SUPPORT';
  const myTeam = user?.user_data?.myTeam;
  if (isStaff && myTeam != null && teamId && Number(teamId) !== Number(myTeam)) {
    throw new Error('You do not have permission to edit this lineup');
  }
  assertLineupsEditable();
}

export type LineupType = 'DH' | 'NINE_MAN';

export type PlayerType = {
  roster_player_id?: number;
  name?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  position?: string;
  position_1?: string;
  role?: string;
  order?: number;
  level?: string;
  status?: string;
  lock?: boolean;
};

export type SimEventRow = {
  roster_player_id?: number;
  mlb_id: number;
  unused_pa_total: number;
  dom_play_totals: Record<string, number>;
};

/** One row from getUnusedBaserunningEvents: unused RW baserunning events per roster row. */
export type SimBaserunningEventRow = {
  roster_player_id?: number;
  mlb_id: number;
  unused_br_total: number;
  dom_play_totals?: Record<string, number>;
};

/** One row from getUnusedFieldingEvents: unused RW fielding events per roster row. */
export type SimFieldingEventRow = {
  roster_player_id?: number;
  mlb_id: number;
  unused_fielding_total: number;
  dom_play_totals?: Record<string, number>;
};

// Hook for managing a team's lineup
export const useTeamLineup = (teamId: string, lineupType: LineupType = 'DH') => {
  const queryClient = useQueryClient();

  // Fetch lineup
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['teamLineup', teamId, lineupType],
    queryFn: async () => {
      if (!teamId) {
        console.warn('No teamId provided to useTeamLineup');
        return { lineup: [], bench: [] };
      }
      try {
        const response = await apiClient.get(`/lineup/team/${teamId}?lineupType=${lineupType}`);
        console.log('lineup response', response.data);
        return response.data || { lineup: [], bench: [] };
      } catch (err) {
        console.error('Error fetching lineup:', err);
        throw err;
      }
    },
    enabled: !!teamId
  });

  // Update lineup
  const { mutate: updateLineup, isPending: isUpdating } = useMutation({
    mutationFn: async (entries: PlayerType[]) => {
      if (!teamId) {
        throw new Error('No teamId provided for lineup update');
      }
      assertCanMutateLineupForTeam(teamId);
      const response = await apiClient.put(`/lineup/team/${teamId}`, {
        lineupType,
        entries
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamLineup', teamId, lineupType] });
    }
  });

  return {
    lineup: data?.lineup || [],
    bench: data?.bench || [],
    simEvents: data?.simEvents || [],
    simBaserunningEvents: data?.simBaserunningEvents || [],
    simFieldingEvents: data?.simFieldingEvents || [],
    locked: data?.locked ?? false,
    isLoading,
    isUpdating,
    error: error as Error | null,
    updateLineup,
    refreshLineup: refetch
  };
};

export type ExtendedBenchResponse = {
  extendedBench: PlayerType[];
  simEvents: SimEventRow[];
  simBaserunningEvents: SimBaserunningEventRow[];
  simFieldingEvents: SimFieldingEventRow[];
  locked?: boolean;
};

/** On-demand fetch for lineup "Load Other Players" (non-MLB batters with unused PA). */
export async function fetchTeamExtendedBench(teamId: string, playerType?: 'pitchers'): Promise<ExtendedBenchResponse> {
  if (!teamId) {
    throw new Error('No teamId provided for extended bench');
  }
  const response = await apiClient.get(`/lineup/team-extended-bench/${teamId}`, {
    params: playerType ? { playerType } : undefined,
  });
  return response.data || {
    extendedBench: [],
    simEvents: [],
    simBaserunningEvents: [],
    simFieldingEvents: [],
  };
};

// Hook for managing a team's pitching rotation
export const useTeamPitchingRotation = (teamId: string) => {
  const queryClient = useQueryClient();

  // Fetch pitching rotation
  const { data: rotation, isLoading, error, refetch } = useQuery({
    queryKey: ['teamPitchingRotation', teamId],
    queryFn: async () => {
      if (!teamId) {
        console.warn('No teamId provided to useTeamPitchingRotation');
        return [];
      }
      try {
        const response = await apiClient.get(`/lineup/pitching/team/${teamId}`);
        return response.data;
      } catch (err) {
        console.error('Error fetching pitching rotation:', err);
        throw err;
      }
    },
    enabled: !!teamId
  });

  // Update pitching rotation
  const { mutate: updateRotation, isPending: isUpdating } = useMutation({
    mutationFn: async (entries: PlayerType[]) => {
      if (!teamId) {
        throw new Error('No teamId provided for rotation update');
      }
      assertCanMutateLineupForTeam(teamId);
      const response = await apiClient.put(`/lineup/pitching/team/${teamId}`, {
        entries
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamPitchingRotation', teamId] });
    }
  });

  return {
    rotation: rotation || {},
    // Pass through simEvents from the API (unused pitcher events summary)
    simEvents: rotation && typeof rotation === 'object' && 'simEvents' in (rotation as any)
      ? ((rotation as any).simEvents || [])
      : [],
    isLoading,
    isUpdating,
    error: error as Error | null,
    updateRotation,
    refreshRotation: refetch
  };
};

export type PHEntry = { roster_player_id: number; situation: 'any' | 'baserunner' | 'rbi' | 'last' | 'unset' };
export type PHSituations = 'any' | 'baserunner' | 'rbi' | 'last' | 'unset';

export type DepthChartType = {
  C: number[];
  '1B': number[];
  '2B': number[];
  '3B': number[];
  SS: number[];
  LF: number[];
  CF: number[];
  RF: number[];
  DH: number[];
  PH: PHEntry[];
  PR: number[];
  hasData: boolean;
};

// Hook for managing a team's depth chart (debounced updates - last write wins)
export const useDepthChart = (teamId: string, lineupType: LineupType = 'DH') => {
  const queryClient = useQueryClient();

  // Fetch depth chart
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['teamDepthChart', teamId, lineupType],
    queryFn: async () => {
      if (!teamId) {
        return {
          C: [], '1B': [], '2B': [], '3B': [], SS: [], LF: [], CF: [], RF: [], DH: [],
          PH: [],
          PR: [],
          hasData: false
        } as DepthChartType;
      }
      try {
        const response = await apiClient.get(`/lineup/depth/team/${teamId}?lineupType=${lineupType}`);
        return response.data as DepthChartType;
      } catch (err) {
        console.error('Error fetching depth chart:', err);
        throw err;
      }
    },
    enabled: !!teamId
  });

  // Update depth chart (idempotent - client should debounce before calling)
  const { mutate: updateDepthChart, isPending: isUpdating } = useMutation({
    mutationFn: async (depthChart: Omit<DepthChartType, 'hasData'>) => {
      if (!teamId) {
        throw new Error('No teamId provided for depth chart update');
      }
      const response = await apiClient.put(`/lineup/depth/team/${teamId}`, {
        lineupType,
        depthChart
      });
      return response.data as DepthChartType;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamDepthChart', teamId, lineupType] });
    }
  });

  return {
    depthChart: data || {
      C: [], '1B': [], '2B': [], '3B': [], SS: [], LF: [], CF: [], RF: [], DH: [], PH: [], PR: [],
      hasData: false
    } as DepthChartType,
    isLoading,
    isUpdating,
    error: error as Error | null,
    updateDepthChart,
    refreshDepthChart: refetch
  };
};

export type PotentialLineupResponse = {
  awayLineup: (Record<string, unknown> | null)[];
  homeLineup: (Record<string, unknown> | null)[];
  awayStarter: unknown;
  homeStarter: unknown;
  success: boolean;
  message?: string;
};


/**
 * Hook to fetch the potential (projected) lineup for a matchup.
 * Returns awayLineup, homeLineup, awayStarter, homeStarter arrays.
 */
// Hook for fetching potential (projected) lineups for a game
export const usePotentialLineup = (
  awayTeamId: number | string | undefined,
  homeTeamId: number | string | undefined,
  leagueId?: number | string,
  gameDate?: string,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled !== false && !!awayTeamId && !!homeTeamId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['potentialLineup', awayTeamId, homeTeamId, leagueId ?? null, gameDate ?? null],
    queryFn: async (): Promise<PotentialLineupResponse> => {
      const response = await apiClient.post('/lineup/potential-lineups', {
        awayTeamId: Number(awayTeamId),
        homeTeamId: Number(homeTeamId),
        ...(leagueId != null ? { leagueId: Number(leagueId) } : {}),
        ...(gameDate ? { gameDate } : {})
      });
      return response.data;
    },
    enabled,
    // Fetch once when enabled; don't refetch on every interaction
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  return {
    data: data ?? null,
    isLoading,
    error: error as Error | null,
    refetch
  };
}; 